import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  MessageCircle,
  Calendar,
  AlertCircle,
  Facebook
} from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';

const ContactPage = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { config } = useConfig();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // In a real app, this would send to your backend
      console.log('Contact form submission:', data);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      reset();
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Phone',
      primary: config?.site?.phone || '(555) 123-4567',
      secondary: 'Monday - Friday, 8:00 AM - 5:00 PM',
      action: `tel:${(config?.site?.phone || '(555) 123-4567').replace(/[^\d]/g, '')}`,
      actionText: 'Call Now'
    },
    {
      icon: Mail,
      title: 'Email',
      primary: 'slmcs.gcs@gmail.com',
      secondary: 'We typically respond within 24 hours',
      action: 'mailto:slmcs.gcs@gmail.com',
      actionText: 'Send Email'
    },
    {
      icon: Facebook,
      title: 'Facebook',
      primary: 'Follow us on Facebook',
      secondary: 'Get updates and announcements',
      action: 'https://www.facebook.com/share/174So3d67f/',
      actionText: 'Visit Page',
      external: true
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      primary: 'Guidance Center Building',
      secondary: 'Main Campus, Ground Floor',
      action: '#location',
      actionText: 'Get Directions'
    },
    {
      icon: Calendar,
      title: 'Schedule',
      primary: 'Book an Appointment',
      secondary: 'Online scheduling available',
      action: '#appointment',
      actionText: 'Schedule Now'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '8:00 AM - 5:00 PM' },
    { day: 'Saturday', hours: '9:00 AM - 12:00 PM' },
    { day: 'Sunday', hours: 'Closed' },
    { day: 'Holidays', hours: 'Closed (See academic calendar)' }
  ];

  const emergencyContacts = [
    {
      title: 'Campus Emergency',
      number: '(555) 123-9999',
      description: 'For immediate campus emergencies'
    },
    {
      title: 'Crisis Hotline',
      number: '988',
      description: 'National Suicide & Crisis Lifeline'
    },
    {
      title: 'After Hours',
      number: '(555) 123-4567',
      description: 'Leave a message for next business day'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-blue-100">
              We're here to help. Reach out to us through any of the methods below, 
              and we'll connect you with the right support.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {contactMethods.map((method, index) => (
              <div key={index} className="card text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <method.icon className="w-8 h-8 text-blue-800" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{method.title}</h3>
                <p className="font-medium text-gray-800 mb-1">{method.primary}</p>
                <p className="text-gray-600 text-sm mb-4">{method.secondary}</p>
                <a 
                  href={method.action}
                  className="btn-outline text-sm"
                  {...(method.external && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {method.actionText}
                </a>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="card">
                <div className="flex items-center mb-6">
                  <MessageCircle className="w-6 h-6 text-blue-800 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-800">Send us a Message</h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        {...register('firstName', { required: 'First name is required' })}
                        placeholder="Enter your first name"
                      />
                      {errors.firstName && (
                        <p className="form-error">{errors.firstName.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        {...register('lastName', { required: 'Last name is required' })}
                        placeholder="Enter your last name"
                      />
                      {errors.lastName && (
                        <p className="form-error">{errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      className="form-input"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="form-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-input"
                      {...register('phone')}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="form-label">Student ID (if applicable)</label>
                    <input
                      type="text"
                      className="form-input"
                      {...register('studentId')}
                      placeholder="Enter your student ID"
                    />
                  </div>

                  <div>
                    <label className="form-label">Subject *</label>
                    <select
                      className="form-input"
                      {...register('subject', { required: 'Please select a subject' })}
                    >
                      <option value="">Select a subject</option>
                      <option value="appointment">Schedule an Appointment</option>
                      <option value="academic">Academic Counseling</option>
                      <option value="career">Career Guidance</option>
                      <option value="personal">Personal Counseling</option>
                      <option value="crisis">Crisis Support</option>
                      <option value="forms">Help with Forms</option>
                      <option value="general">General Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.subject && (
                      <p className="form-error">{errors.subject.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Message *</label>
                    <textarea
                      className="form-input"
                      rows="5"
                      {...register('message', { required: 'Message is required' })}
                      placeholder="Please describe how we can help you..."
                    />
                    {errors.message && (
                      <p className="form-error">{errors.message.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Preferred Contact Method</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="email"
                          {...register('preferredContact')}
                          className="mr-3"
                          defaultChecked
                        />
                        Email
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="phone"
                          {...register('preferredContact')}
                          className="mr-3"
                        />
                        Phone
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="either"
                          {...register('preferredContact')}
                          className="mr-3"
                        />
                        Either is fine
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner w-5 h-5 mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Office Hours */}
              <div className="card">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-blue-800 mr-3" />
                  <h3 className="text-xl font-semibold">Office Hours</h3>
                </div>
                <div className="space-y-3">
                  {officeHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <span className="font-medium text-gray-800">{schedule.day}</span>
                      <span className="text-gray-600">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>Walk-in Hours:</strong> Monday-Friday, 1:00 PM - 4:00 PM
                    (No appointment needed)
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="card" id="location">
                <div className="flex items-center mb-4">
                  <MapPin className="w-6 h-6 text-blue-800 mr-3" />
                  <h3 className="text-xl font-semibold">Our Location</h3>
                </div>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Guidance Center Building</strong></p>
                  <p>Main Campus, Ground Floor</p>
                  <p>{config?.site?.schoolName || 'Your School Name'}</p>
                  <p>
                    Burgos Street, Talisay<br />
                    4700 Sorsogon, Sorsogon
                  </p>
                </div>
                <div className="mt-4">
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Burgos+Street+Talisay+4700+Sorsogon"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </a>
                </div>
              </div>

              {/* Emergency Contacts */}
              <div className="card">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-blue-800 mr-3" />
                  <h3 className="text-xl font-semibold">Emergency Contacts</h3>
                </div>
                <div className="space-y-4">
                  {emergencyContacts.map((contact, index) => (
                    <div key={index} className="border-l-4 border-red-200 pl-4">
                      <h4 className="font-medium text-gray-800">{contact.title}</h4>
                      <p className="text-lg font-semibold text-blue-800">{contact.number}</p>
                      <p className="text-gray-600 text-sm">{contact.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    <strong>Crisis Support:</strong> If you're experiencing a mental health emergency, 
                    please call 988 or visit your nearest emergency room immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Quick answers to common questions about contacting and visiting our guidance center.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Do I need an appointment?</h3>
                <p className="text-gray-600 text-sm">
                  While appointments are recommended, we also offer walk-in hours Monday-Friday 
                  from 1:00 PM - 4:00 PM for urgent matters.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">How quickly will you respond?</h3>
                <p className="text-gray-600 text-sm">
                  We typically respond to emails within 24 hours during business days. 
                  For urgent matters, please call us directly.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Is counseling confidential?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, all counseling services are confidential. Information is only shared 
                  with your consent or when required by law for safety reasons.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Can parents contact you?</h3>
                <p className="text-gray-600 text-sm">
                  Parents are welcome to contact us with concerns. However, we maintain 
                  student confidentiality and may need student consent to share information.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">What if I'm not a student?</h3>
                <p className="text-gray-600 text-sm">
                  Faculty, staff, and parents can contact us for consultation about 
                  student concerns or to learn about our services.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Do you offer virtual appointments?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, we offer virtual counseling sessions via secure video conferencing 
                  when in-person meetings aren't possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;